// Offline support for the technician app.
//
// Crawlspaces, attics and rural routes lose signal. Two things keep a tech
// working through that:
//
//  1. The day's job list is kept on the phone (localStorage) after every
//     successful load, and shown from there when the server can't be reached.
//  2. Anything a tech does without signal — check-in, a device inspection, a
//     job completion with its photos and signature — goes into an outbox in
//     IndexedDB (which can hold the photo files) and is sent, in order, as soon
//     as the phone is back online.
//
// Each outbox item is { id, kind, jobId, payload, files: [{ field, blob, name,
// meta }], createdAt, attempts, lastError }. A server refusal (4xx) is kept with
// its reason so the office can see it; a network failure is simply retried.
import { apiWorkorders, jobApi, uploads, devices, pesticide } from './api';

const DB = 'xppro-offline';
const STORE = 'outbox';
const JOBS_KEY = 'xp_tech_jobs_v1';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const out = fn(store);
    t.oncomplete = () => { db.close(); resolve(out && 'result' in out ? out.result : out); };
    t.onerror = () => { db.close(); reject(t.error); };
  });
}

const listeners = new Set();
let lastCount = 0;
function notify(count) {
  lastCount = count;
  listeners.forEach((fn) => fn(count));
}

/** Subscribe to the number of items waiting; returns an unsubscribe function. */
export function onOutboxChange(fn) {
  listeners.add(fn);
  fn(lastCount);
  return () => listeners.delete(fn);
}

export async function outboxItems() {
  try {
    const items = await tx('readonly', (s) => s.getAll());
    return (items || []).sort((a, b) => a.createdAt - b.createdAt);
  } catch {
    return [];
  }
}

async function refreshCount() {
  notify((await outboxItems()).length);
}

/** A failure worth retrying later: no signal/DNS/timeout, or the server is
 *  restarting behind the proxy (502/503/504). Anything else is a real refusal. */
export const isNetworkError = (err) => !!err && (!err.response || [502, 503, 504].includes(err.response.status));

export async function enqueue(kind, jobId, payload, files = []) {
  const item = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind, jobId, payload, files, createdAt: Date.now(), attempts: 0, lastError: '',
  };
  await tx('readwrite', (s) => s.put(item));
  await refreshCount();
  return item;
}

async function remove(id) {
  await tx('readwrite', (s) => s.delete(id));
}

async function save(item) {
  await tx('readwrite', (s) => s.put(item));
}

async function uploadFiles(item) {
  // Files are uploaded first; their new ids are filled into the payload.
  const ids = {};
  for (const f of item.files || []) {
    if (f.uploadedId) { (ids[f.field] = ids[f.field] || []).push(f.uploadedId); continue; }
    const res = await uploads.upload(new File([f.blob], f.name, { type: f.blob.type || 'image/jpeg' }), f.meta);
    const id = res?.files?.[0]?.id;
    f.uploadedId = id;
    await save(item); // remember progress so a retry never re-uploads a photo
    (ids[f.field] = ids[f.field] || []).push(id);
  }
  return ids;
}

async function send(item) {
  const ids = await uploadFiles(item);
  if (item.kind === 'checkin') {
    await apiWorkorders.update(item.jobId, { ...item.payload });
  } else if (item.kind === 'complete') {
    await jobApi.complete(item.jobId, {
      ...item.payload,
      photoIds: [...(item.payload.photoIds || []), ...(ids.photo || [])],
      signatureId: item.payload.signatureId || (ids.signature || [])[0],
    });
  } else if (item.kind === 'inspection') {
    await devices.inspect(item.payload);
  } else if (item.kind === 'device') {
    const created = await devices.create(item.payload);
    // An inspection queued against this offline-created device now points at its real id.
    const rest = await outboxItems();
    for (const other of rest) {
      if (other.kind === 'inspection' && other.payload.deviceId === item.payload.tempId) {
        other.payload.deviceId = created.id;
        await save(other);
      }
    }
  } else if (item.kind === 'pesticide') {
    await pesticide.create(item.payload);
  } else if (item.kind === 'report') {
    // Queued after its 'complete', so the visit is closed before its report files.
    await jobApi.serviceReport(item.jobId, item.payload);
  }
}

let flushing = false;
/** Send everything waiting, oldest first. Returns { sent, failed, waiting }. */
export async function flushOutbox() {
  if (flushing) return { sent: 0, failed: 0, waiting: lastCount };
  flushing = true;
  let sent = 0;
  let failed = 0;
  try {
    for (const item of await outboxItems()) {
      try {
        await send(item);
        await remove(item.id);
        sent += 1;
      } catch (err) {
        if (isNetworkError(err)) break; // still offline: keep order, try later
        item.attempts += 1;
        item.lastError = err?.response?.data?.detail || `Server refused (${err?.response?.status})`;
        await save(item);
        failed += 1;
      }
    }
  } finally {
    flushing = false;
    await refreshCount();
  }
  return { sent, failed, waiting: lastCount };
}

let started = false;
/** Start background syncing: when the phone comes online and every 30 seconds. */
export function startOutboxSync(onResult) {
  if (started) return;
  started = true;
  const run = async () => {
    if (!navigator.onLine) return;
    const r = await flushOutbox();
    if ((r.sent || r.failed) && onResult) onResult(r);
  };
  window.addEventListener('online', run);
  setInterval(run, 30000);
  refreshCount();
  run();
}

/* ---------------------------------------------------------------- job list cache */
export function cacheJobs(jobs) {
  try { localStorage.setItem(JOBS_KEY, JSON.stringify({ at: Date.now(), jobs })); } catch { /* storage full: skip */ }
}

export function cachedJobs() {
  try {
    const raw = JSON.parse(localStorage.getItem(JOBS_KEY) || 'null');
    return raw && Array.isArray(raw.jobs) ? raw : null;
  } catch {
    return null;
  }
}
